import ComponentBase from '../base/component-base';
import {
  EVENT_CART_OPERATION_START,
  EVENT_CART_OPERATION_END,
  EVENT_GLOBAL_CART_ACTIVATE,
  EVENT_CART_CONTENT_LOADED,
} from '../../constants/event';
import Cart from '../../libs/cart';
import { CartAddItem } from '../../typs/cart';

/**
 * Add to Cart Button Component
 * Handles individual add to cart button functionality
 * Multiple instances can exist on the same page
 */
class AddCartButton extends ComponentBase {
  private form: HTMLFormElement | null = null;
  private isLoading: boolean = false;
  private buttonElement: HTMLButtonElement | null = null;
  private onSubmitBinding: (e: Event) => void;
  private onCartOperationStartBinding: () => void;
  private onCartOperationEndBinding: () => void;

  constructor(element: HTMLElement) {
    super(element);

    console.log('[AddCartButton] Initializing button:', element);

    // Bind event handlers
    this.onSubmitBinding = this.onSubmit.bind(this);
    this.onCartOperationStartBinding = this.onCartOperationStart.bind(this);
    this.onCartOperationEndBinding = this.onCartOperationEnd.bind(this);

    this.init();
  }

  protected init(): void {
    // Store button element
    this.buttonElement = this.element as HTMLButtonElement;

    // Find the parent form
    this.form = this.element.closest('form');
    if (!this.form) {
      console.warn('[AddCartButton] Add to cart button is not inside a form', this.element);
      return;
    }

    console.log('[AddCartButton] Form found:', this.form);

    // Add event listeners
    this.form.addEventListener('submit', this.onSubmitBinding);
    document.addEventListener(EVENT_CART_OPERATION_START, this.onCartOperationStartBinding);
    document.addEventListener(EVENT_CART_OPERATION_END, this.onCartOperationEndBinding);

    console.log('[AddCartButton] Event listeners attached');
  }

  private async onSubmit(e: Event): Promise<void> {
    e.preventDefault();

    console.log('[AddCartButton] Form submitted');

    if (this.isLoading || !this.form) {
      console.log('[AddCartButton] Skipping - isLoading:', this.isLoading, 'form:', this.form);
      return;
    }

    try {
      // Get form data
      const formData = new FormData(this.form);
      const variantId = formData.get('id');
      const quantity = formData.get('quantity');

      console.log('[AddCartButton] Form data - variantId:', variantId, 'quantity:', quantity);

      if (!variantId) {
        console.error('[AddCartButton] Variant ID is required');
        return;
      }

      // Prepare cart item
      const item: CartAddItem = {
        id: variantId as string,
        quantity: quantity ? parseInt(quantity as string, 10) : 1,
      };

      console.log('[AddCartButton] Adding to cart:', item);

      // Add to cart
      const result = await Cart.add(item);

      console.log('[AddCartButton] Added to cart successfully:', result);

      // Wait for cart content to be loaded before showing modal
      console.log('[AddCartButton] Waiting for cart content to load...');

      // Listen for cart content loaded event
      const cartContentLoadedHandler = () => {
        console.log('[AddCartButton] Cart content loaded, opening modal');
        document.dispatchEvent(new CustomEvent(EVENT_GLOBAL_CART_ACTIVATE));
      };

      document.addEventListener(EVENT_CART_CONTENT_LOADED, cartContentLoadedHandler, { once: true });
    } catch (error) {
      console.error('[AddCartButton] Error adding to cart:', error);
      // You can dispatch a custom error event here if needed
    }
  }

  private onCartOperationStart(): void {
    this.isLoading = true;
    if (this.buttonElement) {
      this.buttonElement.disabled = true;
    }
    this.element.classList.add('is-loading');
  }

  private onCartOperationEnd(): void {
    this.isLoading = false;
    if (this.buttonElement) {
      this.buttonElement.disabled = false;
    }
    this.element.classList.remove('is-loading');
  }

  protected onDestroy(): void {
    super.onDestroy();

    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmitBinding);
    }

    document.removeEventListener(EVENT_CART_OPERATION_START, this.onCartOperationStartBinding);
    document.removeEventListener(EVENT_CART_OPERATION_END, this.onCartOperationEndBinding);
  }
}

export default AddCartButton;
